<template>
  <section class="ma-4 ma-md-8">
    <t-page-title
      v-if="can('create', 'banners')"
      title="Banners"
      :on-add="() => $router.push({ name: 'create-banner' })"
    />
    <t-page-title v-else title="Banners" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`item.publish_at`]="{ item }">
            <span> {{ fromSQL(item.publish_at, 'dd/MM/yyyy HH:mm') }}</span>
          </template>
          <template #[`item.unpublish_at`]="{ item }">
            <span> {{ fromSQL(item.unpublish_at, 'dd/MM/yyyy HH:mm') }}</span>
          </template>
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.status`]="{ item }">
            <span> {{ item.status === 'active' ? 'Ativo' : 'Inativo' }}</span>
          </template>
          <template #[`item.platform`]="{ item }">
            <span class="text-capitalize"> {{ item.platform }}</span>
          </template>
          <template #[`item.category`]="{ item }">
            <span> {{ item.category === 'external' ? 'Externo' : 'Interno' }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>

import TPageTitle from '@/components/TPageTitle.vue'
import type { DataTableFilter, DataTableHeader, DefaultDataTableActions } from '@/types/DataTable'
import BannerService from '@/services/BannerService'
import { Banner } from '@/types/Banner'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { computed, Ref, ref, useTemplateRef } from 'vue'
import { useAbility } from '@casl/vue'
import { useRouter } from 'vue-router'
import { Resources } from '@/plugins/permissions/typing'
import { AxiosError } from 'axios'
import DataTable from '@/components/datatable/DataTable.vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useProducerFilter } from '@/helpers/reports/producerFilter'
import { fromSQL } from '@/plugins/date'

const { can } = useAbility()
const router = useRouter()
const loader = useLoader()
const snackbar = useSnackBar()

const datatable = useTemplateRef('datatable')

const resource: Resources = 'banners'

const headers = computed((): DataTableHeader[] => [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Titulo', value: 'title', key: 'title', sortable: false },
  { title: 'Produtor', value: 'producer.display_name', key: 'producer.display_name', sortable: false },
  { title: 'Plataforma', value: 'platform', key: 'platform', sortable: false },
  { title: 'Categoria', value: 'category', key: 'category', sortable: false },
  { title: 'Publicar em', value: 'publish_at', key: 'publish_at', sortable: false },
  { title: 'Despublicar em', value: 'unpublish_at', key: 'unpublish_at', sortable: false },
  { title: 'Status', value: 'status', key: 'status', sortable: false },
  { title: 'Ações', value: 'actions', key: 'actions', sortable: false, align: 'center' },
])

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({ label: 'Título', field: 'title', required: false }),
  useProducerFilter({
    label: 'Produtor',
    field: 'producer_id',
    icon: 'fas fa-shield',
    initial: null,
  }),
  useSelectFilter({
    label: 'Categoria',
    field: 'category',
    initial: '',
    items: [
      { title: 'Todas', value: '' },
      { title: 'Interno', value: '0' },
      { title: 'Externo', value: '1' },
    ],
    icon: '',
    required: false,
  }),
  useSelectFilter({
    field: 'status',
    label: 'Status',
    initial: 'true',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Ativo', value: 'true' },
      { title: 'Inativo', value: 'false' },
    ],
    icon: '',
    required: false,
  }),
  useSelectFilter({
    field: 'platform',
    label: 'Plataforma',
    initial: '',
    items: [
      { title: 'Todas', value: '' },
      { title: 'Desktop', value: 'desktop' },
      { title: 'Mobile', value: 'mobile' },
    ],
    icon: '',
    required: false,
  }),
])

const actions = computed((): DefaultDataTableActions => ({
  update: {
    callback: (item: Banner) => router.push({ name: 'edit-banner', params: { id: item.id } }),
    condition: () => can('update', 'banners'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: Banner) => destroy(item),
    condition: () => can('delete', 'banners'),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}))

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    const response = await (new BannerService()).index(page, filters, per_page)
    return response
  } catch (error) {
    if (error instanceof AxiosError) {
      snackbar.show(error.response?.data?.message, 'error')
    } else if (error instanceof Error) {
      snackbar.show(error.message, 'error')
    } else {
      snackbar.show('Não foi possível atualizar as informações do banner', 'error')
    }
    throw error
  }
}

async function destroy(item: Banner) {
  try {
    loader.increment()
    await (new BannerService()).destroy(item.id)

    snackbar.show(
      'Banner excluído com sucesso.',
      'success',
    )

    if (datatable.value !== null) {
      datatable.value.fetch(1)
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      snackbar.show(error.response?.data?.message, 'error')
    } else if (error instanceof Error) {
      snackbar.show(error.message, 'error')
    } else {
      snackbar.show('Não foi possível atualizar as informações do banner', 'error')
    }
  } finally {
    loader.decrement()
  }
}

</script>
